import NavigationItemButton from '@client/app/component/navigation-item-button.tsx';
import { forceUpdateServiceWorker } from '@client/app/logic/force-update.ts';
import { UserCompanyPermission } from '@client/graphql/types/graphql.ts';
import { useAuth } from '@client/module/auth/auth-context.ts';
import useCheckAdminRole from '@client/module/auth/check-role-hook.ts';
import checkPermission from '@client/module/utils/check-permission.ts';
import { convertRoleConfigToUserRoles, hasRequiredRole } from '@client/module/utils/check-role';
import { useCompanyHubpageLink } from '@client/page/management/company-settings/logic/use-company-hubpage-link.ts';
import FocusedBranchSelection from '@client/page/management/table-group/table-group-list/component/branch-selection/focused-branch-selection.tsx';
import SidebarLanguageSelector from '@client/app/component/sidebar-language-selector.tsx';
import {
  BeakerIcon,
  BookOpenIcon,
  ChevronLeftIcon,
  CloudIcon,
  CodeBracketIcon,
  CursorArrowRippleIcon,
  ServerIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/16/solid';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  AdjustmentsHorizontalIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  BanknotesIcon,
  CalendarIcon,
  ChartBarSquareIcon,
  ClockIcon,
  Cog6ToothIcon,
  FaceSmileIcon,
  FilmIcon,
  HomeIcon,
  KeyIcon,
  LinkIcon,
  PrinterIcon,
  RectangleStackIcon,
  ShoppingBagIcon,
  Square3Stack3DIcon,
  TruckIcon,
  UsersIcon,
  WalletIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { ArrowsPointingInIcon } from '@heroicons/react/24/solid';
import { useFocusedCompany } from '@packages/core/company/focused-company-context.ts';
import getImageLink from '@packages/core/image-source/get-image-link.ts';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import { useTranslate } from '@tolgee/react';
import classNames from 'classnames';
import { createDomMotionComponent } from 'framer-motion';
import { ComponentProps, ComponentType, ReactNode, useEffect, useState } from 'react';
import { Link, useMatches, useNavigate } from 'react-router-dom';

export interface NavigationItem {
  title: string;
  href: string;
  icon: ComponentType<ComponentProps<'svg'>>;
  permission?: UserCompanyPermission;
  permissions?: UserCompanyPermission[];
  query?: Record<string, string> | (() => Record<string, string>);
  admin?: boolean;
  superAdmin?: boolean;
  children?: NavigationItem[];
  wrapper?: (wrappedComponent: ReactNode) => JSX.Element;
}

const motion = { div: createDomMotionComponent('div'), ul: createDomMotionComponent('ul') };

const HubPageCustomNavigationItem = (wrappedComponent: ReactNode) => {
  const link = useCompanyHubpageLink();
  return (
    <div
      onClick={(event) => {
        event.preventDefault();
        window.open(link, '_blank', 'noopener, noreferrer');
      }}
    >
      {wrappedComponent}
    </div>
  );
};

// const DigitalMenuCustomNavigationItem = (wrappedComponent: ReactNode) => {
//   const link = import.meta.env.VITE_DIGITAL_MENU_APP as string;
//
//   return (
//     <div
//       onClick={(event) => {
//         event.preventDefault();
//         // Mở link đầy đủ (không nối thêm gì cả)
//         window.open(link.startsWith('http') ? link : `https://${link}`, '_blank', 'noopener,noreferrer');
//       }}
//     >
//       {wrappedComponent}
//     </div>
//   );
// };

//HOME PAGE
const dashboardHome: NavigationItem[] = [
  {
    title: 'Dashboard',
    href: '/dashboard',
    icon: HomeIcon,
  },
  {
    title: 'Business Card',
    href: '',
    icon: LinkIcon,
    wrapper: HubPageCustomNavigationItem,
  },
  {
    title: 'Customer',
    href: '/customer',
    icon: UsersIcon,
    permission: UserCompanyPermission.ManageNailAppointment,
    admin: true,
  },
];

const reservationHome: NavigationItem[] = [
  {
    title: 'Schedule',
    href: '/table-reservation/list',
    icon: CalendarIcon,
    permission: UserCompanyPermission.ManageTableReservation,
  },
  {
    title: 'Report',
    href: '/report/reservation',
    icon: ChartBarSquareIcon,
    permission: UserCompanyPermission.ManageTableReservation,
  },
  {
    title: 'History',
    href: '/table-reservation/history',
    icon: ArchiveBoxIcon,
    permission: UserCompanyPermission.ManageTableReservation,
  },
];

const onlineOrderHome: NavigationItem[] = [
  {
    title: 'Orders',
    href: '/online-order/dashboard',
    icon: WalletIcon,
    permission: UserCompanyPermission.ManageOnlineOrder,
  },
  {
    title: 'Menu',
    href: '/menu',
    icon: Square3Stack3DIcon,
    permission: UserCompanyPermission.ManageOnlineOrder,
  },
  {
    title: 'Allergy',
    href: '/allergy',
    icon: BeakerIcon,
    permissions: [UserCompanyPermission.RestaurantOwner, UserCompanyPermission.ManageOnlineOrder],
  },
  {
    title: 'Report',
    href: '/report/online-order',
    icon: ChartBarSquareIcon,
    permissions: [UserCompanyPermission.RestaurantOwner, UserCompanyPermission.ManageOnlineOrder],
  },
  {
    title: 'History',
    href: '/online-order/history',
    icon: ArchiveBoxIcon,
    permission: UserCompanyPermission.ManageOnlineOrder,
  },
];

const appointmentHome: NavigationItem[] = [
  {
    title: 'Appointment List',
    href: '/appointment/list',
    icon: CalendarIcon,
    permission: UserCompanyPermission.ManageNailAppointment,
  },
  {
    title: 'Service Menu',
    href: '/appointment/service',
    icon: Square3Stack3DIcon,
    permission: UserCompanyPermission.ManageNailAppointment,
  },
  {
    title: 'Employee',
    href: '/appointment/employee',
    icon: UserIcon,
    permission: UserCompanyPermission.ManageNailAppointment,
  },
  {
    title: 'Report',
    href: '/report/appointment',
    icon: ChartBarSquareIcon,
    permission: UserCompanyPermission.ManageNailAppointment,
  },
  {
    title: 'History',
    href: '/appointment/history',
    icon: ArchiveBoxIcon,
    permission: UserCompanyPermission.ManageNailAppointment,
  },
];

const customerHome: NavigationItem[] = [
  {
    title: 'Customer',
    href: '/customer',
    icon: UsersIcon,
    permission: UserCompanyPermission.ManageNailAppointment,
  },
];

const featureHome: NavigationItem[] = [
  {
    title: 'Banner',
    href: '/banners',
    icon: FilmIcon,
    permission: UserCompanyPermission.RestaurantOwner,
  },
  {
    title: 'Invoice',
    href: '/invoices',
    icon: BanknotesIcon,
    permission: UserCompanyPermission.ManageInvoices,
  },
];

// SETTINGS PAGE
const generalSetting: NavigationItem[] = [
  {
    title: 'Users',
    href: '/user-company-roles',
    icon: UsersIcon,
    permission: UserCompanyPermission.ManageUsers,
  },
  {
    title: 'Team',
    href: '/team',
    icon: UserGroupIcon,
    permission: UserCompanyPermission.ManageUsers,
  },
  {
    title: 'Location',
    href: '/branches',
    icon: RectangleStackIcon,
    permission: UserCompanyPermission.ManageBranches,
  },
  {
    title: 'Company',
    href: '/settings/company',
    icon: ArrowsPointingInIcon,
    permission: UserCompanyPermission.ManageTables,
  },
  {
    title: 'Opening time',
    href: '/settings/opening-time',
    icon: ClockIcon,
    permission: UserCompanyPermission.ManageOpeningTime,
  },
  {
    title: 'Holiday',
    href: '/settings/holiday',
    icon: CloudIcon,
    permission: UserCompanyPermission.ManageOpeningTime,
  },
  {
    title: 'API Key',
    href: '/api-key/internal',
    icon: KeyIcon,
    superAdmin: true,
  },
  {
    title: 'Stripe API key',
    href: '/stripe/api-key',
    icon: CodeBracketIcon,
    permission: UserCompanyPermission.RestaurantOwner,
  },

  {
    title: 'HubPage',
    href: '/settings/hubpage',
    icon: LinkIcon,
    permission: UserCompanyPermission.ManageHubpage,
  },
  {
    title: 'Logging',
    href: '/logging',
    icon: BookOpenIcon,
    admin: true,
  },
];

const reservationSetting: NavigationItem[] = [
  {
    title: 'General',
    href: '/table-reservation/preferences/general',
    icon: AdjustmentsHorizontalIcon,
    permission: UserCompanyPermission.ManageTableReservation,
  },
  {
    title: 'Advanced',
    href: '/table-reservation/preferences/advanced',
    icon: WrenchScrewdriverIcon,
    permission: UserCompanyPermission.ManageTableReservation,
  },
  {
    title: 'Custom notification',
    href: '/table-reservation/preferences/notification',
    icon: ArchiveBoxIcon,
    permission: UserCompanyPermission.ManageTableReservation,
  },
  {
    title: 'Capacity',
    href: '/table-reservation/preferences/capacity',
    icon: ServerIcon,
    permission: UserCompanyPermission.ManageTableReservation,
  },
  {
    title: 'Time',
    href: '/table-reservation/preferences/opening-time',
    icon: ClockIcon,
    permission: UserCompanyPermission.ManageTableReservation,
  },
];

const integrationSettings: NavigationItem[] = [
  {
    title: 'Google',
    href: '/settings/integration/google',
    icon: ArrowsPointingInIcon,
  },
];

const onlineOrderSetting: NavigationItem[] = [
  {
    title: 'General',
    href: '/online-order/setting/general',
    icon: AdjustmentsHorizontalIcon,
    permission: UserCompanyPermission.ManageOnlineOrder,
  },
  {
    title: 'Pick up',
    href: '/online-order/setting/pick-up',
    icon: ShoppingBagIcon,
    permission: UserCompanyPermission.ManageOnlineOrder,
  },
  {
    title: 'Delivery',
    href: '/online-order/setting/delivery',
    icon: TruckIcon,
    permission: UserCompanyPermission.ManageOnlineOrder,
  },
  {
    title: 'Happy hour',
    href: '/online-order/happy-hour',
    icon: FaceSmileIcon,
    permission: UserCompanyPermission.RestaurantOwner,
  },
  {
    title: 'Capacity',
    href: '/online-order/setting/capacity',
    icon: ServerIcon,
    permission: UserCompanyPermission.ManageTableReservation,
  },
  {
    title: 'Payment',
    href: '/online-order/setting/payment',
    icon: CodeBracketIcon,
    permission: UserCompanyPermission.RestaurantOwner,
  },
  {
    title: 'Online Payment',
    href: '/online-order/setting/online-payment',
    icon: CodeBracketIcon,
    permission: UserCompanyPermission.RestaurantOwner,
  },
  {
    title: 'Menu Print rule',
    href: '/print-rule/menu?service=onlineOrder',
    icon: BookOpenIcon,
    permission: UserCompanyPermission.RestaurantOwner,
  },
];

const appointmentSetting: NavigationItem[] = [
  {
    title: 'General',
    href: '/appointment/configuration/general',
    icon: AdjustmentsHorizontalIcon,
    permission: UserCompanyPermission.ManageNailAppointment,
  },
  {
    title: 'Capacity',
    href: '/appointment/configuration/capacity',
    icon: ServerIcon,
    permission: UserCompanyPermission.ManageTableReservation,
  },
  {
    title: 'Time',
    href: '/appointment/configuration/opening-time',
    icon: ClockIcon,
    permission: UserCompanyPermission.ManageNailAppointment,
  },
];

const printerSetting: NavigationItem[] = [
  {
    title: 'Epson Server Direct Print',
    href: '/printer/setting/epson-server-direct-print',
    icon: PrinterIcon,
    admin: true,
  },
  {
    title: 'Printer',
    href: '/online-order/setting/printer',
    icon: PrinterIcon,
    permission: UserCompanyPermission.ManageOnlineOrder,
  },
];

enum NavigationMode {
  WORKING_MODE,
  SETTING_MODE,
}

function NavigationContent(props: { closeSidebar?: () => void }) {
  const matches = useMatches();
  const navigate = useNavigate();
  const company = useFocusedCompany();
  const { t } = useTranslate();
  const { isSuperAdmin } = useCheckAdminRole();
  // save on local storage, not save on state like this
  const [navigationMode, setNavigationMode] = useState<NavigationMode>(() => {
    const currentPage = localStorage.getItem('navigationModeOfCurrentPage');
    if (currentPage) {
      return currentPage === NavigationMode.SETTING_MODE.toString()
        ? NavigationMode.SETTING_MODE
        : NavigationMode.WORKING_MODE;
    }

    const savedMode = localStorage.getItem('navigationMode');
    return savedMode === NavigationMode.SETTING_MODE.toString()
      ? NavigationMode.SETTING_MODE
      : NavigationMode.WORKING_MODE;
  });

  const currentLocation = (path: string): boolean => {
    for (const match of matches.slice(4, 5)) {
      if (match.pathname === path) {
        return true;
      }
    }
    return false;
  };

  const setMode = (mode: NavigationMode) => {
    localStorage.setItem('navigationModeOfCurrentPage', mode.toString());
    setNavigationMode(mode);
    localStorage.setItem('navigationMode', mode.toString());
  };

  const changeMode = () => {
    if (navigationMode === NavigationMode.WORKING_MODE) {
      setNavigationMode(NavigationMode.SETTING_MODE);
      localStorage.setItem('navigationMode', NavigationMode.SETTING_MODE.toString());
    }
    if (navigationMode === NavigationMode.SETTING_MODE) {
      setNavigationMode(NavigationMode.WORKING_MODE);
      localStorage.setItem('navigationMode', NavigationMode.WORKING_MODE.toString());
    }
  };

  return (
    <ul role="list" className="flex flex-1 flex-col justify-between gap-y-7">
      <div className="flex flex-col gap-y-7">
        <div
          className="flex items-center space-x-2 cursor-pointer"
          onClick={() => {
            navigate('/company-selection');
          }}
        >
          {company.logo && <img src={getImageLink(company.logo)} alt={company.name} className="w-6 h-6 rounded" />}
          <p className="text-lg font-medium text-white">{company.name}</p>
        </div>

        {navigationMode === NavigationMode.WORKING_MODE && (
          <>
            <FocusedBranchSelection />
            <div className="flex flex-col gap-3 -ml-4">
              <NavigationItemList
                title="Dashboard"
                items={dashboardHome}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.WORKING_MODE}
              />

              <NavigationItemList
                title="Reservation"
                items={reservationHome}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.WORKING_MODE}
              />
              <NavigationItemList
                title="Online order"
                items={onlineOrderHome}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.WORKING_MODE}
              />
              <NavigationItemList
                title="Appointment"
                items={appointmentHome}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.WORKING_MODE}
              />

              <NavigationItemList
                title="Customer"
                items={customerHome}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.WORKING_MODE}
              />
              <NavigationItemList
                title="Features"
                items={featureHome}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.WORKING_MODE}
              />
            </div>
          </>
        )}

        {navigationMode === NavigationMode.SETTING_MODE && (
          <>
            <li>
              <div
                onClick={() => {
                  changeMode();
                }}
                className={classNames(
                  'group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                  'text-gray-100 bg-gray-700 hover:bg-gray-500 hover:text-gray-100 cursor-pointer',
                )}
              >
                <ArrowUturnLeftIcon
                  className={classNames('h-5 w-6 shrink-0 text-white group-hover:text-white', {
                    'text-white': currentLocation('/settings'),
                  })}
                  aria-hidden="true"
                />
                <span>{t('navigation-content.setting.return-button', 'Back')}</span>
              </div>
            </li>
            <div className="flex flex-col gap-3 -ml-4">
              <NavigationItemList
                title="General"
                items={generalSetting}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.SETTING_MODE}
              />
              <NavigationItemList
                title="Reservation"
                items={reservationSetting}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.SETTING_MODE}
              />
              <NavigationItemList
                title="Online Order"
                items={onlineOrderSetting}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.SETTING_MODE}
              />

              <NavigationItemList
                title="Appointment"
                items={appointmentSetting}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.SETTING_MODE}
              />

              <NavigationItemList
                title="Integration"
                items={integrationSettings}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.SETTING_MODE}
              />

              <NavigationItemList
                title="Printer"
                items={printerSetting}
                closeSidebar={props.closeSidebar}
                navigationMode={NavigationMode.SETTING_MODE}
              />

              {isSuperAdmin && (
                <Link to="/admin-configuration">
                  <div
                    onClick={() => {
                      setMode(NavigationMode.SETTING_MODE);
                    }}
                    className={classNames(
                      'group -mx-2 flex gap-x-1 rounded-md ml-4 p-2 text-lg  font-medium leading-6 text-white hover:bg-gray-500 hover:text-white',
                      {
                        'bg-gray-500 text-white': currentLocation('/admin-configuration'),
                      },
                    )}
                  >
                    <CursorArrowRippleIcon
                      className={classNames('h-6 w-6 shrink-0 text-white group-hover:text-white', {
                        'text-white': currentLocation('/settings'),
                      })}
                      aria-hidden="true"
                    />
                    {t('navigation-content.setting.admin-configuration', 'Admin configuration')}
                  </div>
                </Link>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-y-6">
        {navigationMode === NavigationMode.WORKING_MODE && (
          <li>
            <div
              onClick={() => {
                changeMode();
              }}
              className={classNames(
                'group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:bg-gray-500 hover:text-white',
                {
                  'bg-gray-500 text-white': currentLocation('/settings'),
                },
              )}
            >
              <Cog6ToothIcon
                className={classNames('h-6 w-6 shrink-0 text-white group-hover:text-white', {
                  'text-white': currentLocation('/settings'),
                })}
                aria-hidden="true"
              />
              Setting
            </div>
          </li>
        )}
        <SidebarLanguageSelector />
        <div className="flex items-center justify-between">
          <SecondaryButton
            onClick={() => {
              forceUpdateServiceWorker().catch((e) => void e);
            }}
          >
            Force update
          </SecondaryButton>

          <p className="text-xs text-gray-300">version: {import.meta.env.VITE_APP_VERSION}</p>
        </div>
      </div>
    </ul>
  );
}

function NavigationItemList(props: {
  title?: string;
  items: NavigationItem[];
  closeSidebar?: () => void;
  navigationMode: NavigationMode;
}) {
  const items = props.items;
  const matches = useMatches();
  const { t } = useTranslate();

  const featTitle = props.title ?? '';
  const [subMenuOpen, setSubMenuOpen] = useState(() => {
    const currentSection = localStorage.getItem('currentSection');
    return currentSection == props.title;
  });

  const { auth } = useAuth();
  const company = useFocusedCompany();

  const currentLocation = (path: string): boolean => {
    const additionalPath = path.match(/\//g)?.length ?? 0;

    for (const match of matches.slice(3 + additionalPath, 4 + additionalPath)) {
      if (match.pathname === path) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    const savedState = localStorage.getItem(`submenu-${featTitle}`);
    if (savedState === 'true') {
      setSubMenuOpen(true);
    }
  }, [featTitle]);

  const filterItem = items.filter((item) => {
    // Check by role
    const roleCheck = hasRequiredRole(
      convertRoleConfigToUserRoles({ admin: item.admin, superAdmin: item.superAdmin }),
      auth,
    );

    if (roleCheck) {
      return true;
    }

    // Check by permissions
    if (item.permissions) {
      for (const permission of item.permissions) {
        if (checkPermission(permission, company.id, auth)) {
          return true;
        }
      }
    }

    // Check by permission
    if (item.permission) {
      return checkPermission(item.permission, company.id, auth);
    }

    // other case - only show if item has no requirements at all
    if (!item.admin && !item.superAdmin && !item.permission && !item.permissions) {
      return true;
    }
    return false;
  });

  const currentPage = () => {
    localStorage.setItem('navigationModeOfCurrentPage', props.navigationMode.toString());
    localStorage.setItem('currentSection', props.title ?? '');
  };

  if (filterItem.length == 0) {
    return <></>;
  }

  if (props.title == 'Dashboard' || props.title == 'Local order') {
    return (
      <li>
        <div className="flex items-center cursor-pointer pb-4">
          <span className="text-lg text-white font-medium pl-4">
            {t('navigation-content.' + props.title + '.title', props.title)}
          </span>
        </div>
        <ul role="list" className=" space-y-1">
          {filterItem.map((item, index) => {
            const navigationItem = (
              <NavigationItemButton
                key={index}
                item={item}
                onClick={() => {
                  props.closeSidebar && props.closeSidebar();
                  currentPage();
                }}
                currentLocation={currentLocation}
                featTitle={featTitle}
              />
            );

            if (item.wrapper) return item.wrapper(navigationItem);

            return navigationItem;
          })}
        </ul>
      </li>
    );
  }

  return (
    <li>
      {props.title && (
        <div
          className="flex items-center cursor-pointer pb-4 pt-2"
          onClick={() => {
            setSubMenuOpen(!subMenuOpen);
          }}
        >
          {subMenuOpen ? (
            <ChevronDownIcon
              className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-indigo-600 ml-2"
              aria-hidden="true"
            />
          ) : (
            <ChevronLeftIcon
              className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-indigo-600 ml-2 transform rotate-180"
              aria-hidden="true"
            />
          )}
          <span className="text-lg text-white font-medium ">
            {t('navigation-content.' + props.title + '.title', props.title)}
          </span>
        </div>
      )}
      <motion.ul
        animate={
          subMenuOpen
            ? {
                height: 'fit-content',
              }
            : {
                height: 0,
              }
        }
        className="flex h-0 flex-col pl-14 text-[0.8rem] font-normal overflow-hidden"
      >
        <ul role="list" className="-mx-8 space-y-1">
          {filterItem.map((item) => (
            <li key={item.title}>
              {item.children ? (
                <motion.div
                  animate={
                    subMenuOpen
                      ? {
                          height: 'fit-content',
                        }
                      : {
                          height: 0,
                        }
                  }
                  className="flex h-0 flex-col  text-[0.8rem] font-normal overflow-hidden"
                >
                  <NavigationItemList
                    title={item.title}
                    items={item.children}
                    closeSidebar={props.closeSidebar}
                    navigationMode={props.navigationMode}
                  />
                </motion.div>
              ) : (
                <Link
                  to={item.href}
                  onClick={() => {
                    props.closeSidebar && props.closeSidebar();
                    currentPage();
                  }}
                  className={classNames(
                    currentLocation(item.href)
                      ? 'bg-gray-500 text-white'
                      : 'text-white hover:text-white hover:bg-gray-500',
                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold max-w-56',
                  )}
                >
                  <item.icon
                    className={classNames(
                      currentLocation(item.href) ? 'text-white' : 'text-gray-500 group-hover:text-white',
                      'h-6 w-6 shrink-0',
                    )}
                    aria-hidden="true"
                  />
                  <span> {t('navigation-content.' + featTitle + '.item.' + item.title, item.title)}</span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      </motion.ul>
    </li>
  );
}

export default NavigationContent;
